import * as moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { TimerService } from '@/shared/services/timerService';

/**
 * Cosmetic grace window (ms) between a lot's clock hitting zero and the UI
 * flipping it to "closed". During this beat the lot shows a pulsing "0s" /
 * 00:00:00 with bidding already disabled, then closes.
 */
export const LOT_GRACE_MS: number = 3000;

/**
 * How long (ms) a just-closed lot's "Closed" state stays visible after the
 * grace window before its row starts fading out (when closed lots are hidden).
 */
export const LOT_CLOSED_LINGER_MS: number = 1200;

function msUntil(datetime: string | null | undefined, nowMs: number): number {
    return datetime ? new Date(datetime).getTime() - nowMs : NaN;
}

export function hasTimePassed(
    datetime: string | null | undefined,
    nowMs: number,
): boolean {
    return msUntil(datetime, nowMs) <= 0;
}

export function isTimeAhead(
    datetime: string | null | undefined,
    nowMs: number,
): boolean {
    return msUntil(datetime, nowMs) > 0;
}

export interface LotTimes {
    auctionLotId: string;
    fromDatetime?: string | null;
    toDatetime?: string | null;
}

function formatCountdown(
    targetUnix: number,
    nowUnix: number,
    isUpcoming: boolean,
): string {
    const totalSeconds: number = Math.max(targetUnix - nowUnix, 0);

    if (totalSeconds <= 0 && !isUpcoming) {
        return '';
    }

    const days: number = Math.floor(totalSeconds / 86400);
    const hours: number = Math.floor(totalSeconds / 3600) % 24;
    const minutes: number = Math.floor(totalSeconds / 60) % 60;
    const seconds: number = totalSeconds % 60;

    if (days > 0) {
        return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    }
    return `${hours}h ${minutes}m ${seconds}s`;
}

/**
 * Drives countdown strings for a set of lots and exposes them by auctionLotId.
 * The owning page should call setLots() when its data changes and stop() on destroy.
 */
export class LotCountdownTracker {
    countdowns: Record<string, string> = {};
    /** Raw seconds remaining per lot — drives numeric consumers (e.g. vb-bid-panel). */
    secondsLeft: Record<string, number> = {};

    /** Emits an auctionLotId the tick its end clock crosses zero (it was still
     *  counting down on the previous tick). Client-clock driven, so it fires
     *  even when no socket traffic touches the lot — unlike
     *  TimerService.timerComplete$, which needs a bid/snipe event to have
     *  registered a timer. */
    private readonly lotEndedSubject: Subject<string> = new Subject<string>();
    readonly lotEnded$ = this.lotEndedSubject.asObservable();

    private lots: LotTimes[] = [];
    private readonly announcedEnds: Map<string, string> = new Map<
        string,
        string
    >();
    private intervalHandle: number | null = null;
    private timerResetSub: Subscription | null = null;

    constructor(
        private timerService: TimerService,
        private readonly now: () => number,
    ) {}

    start(): void {
        if (this.intervalHandle !== null) return;
        this.tick();
        this.intervalHandle = window.setInterval(() => this.tick(), 1000);
        this.timerResetSub = this.timerService.timerReset$.subscribe(
            ({ id, newEndTime }: { id: string; newEndTime: string }) => {
                const target: LotTimes | undefined = this.lots.find(
                    (l: LotTimes) => l.auctionLotId === id,
                );
                if (target) {
                    target.toDatetime = newEndTime;
                    this.tick();
                }
            },
        );
    }

    stop(): void {
        if (this.intervalHandle !== null) {
            window.clearInterval(this.intervalHandle);
            this.intervalHandle = null;
        }
        this.timerResetSub?.unsubscribe();
        this.timerResetSub = null;
    }

    setLots(lots: LotTimes[]): void {
        this.lots = lots;
        this.tick();
    }

    private tick(): void {
        const nowUnix: number = Math.floor(this.now() / 1000);
        const next: Record<string, string> = {};
        const nextSeconds: Record<string, number> = {};
        const endedLotIds: string[] = [];
        for (const lot of this.lots) {
            if (!lot.auctionLotId) continue;
            const isUpcoming: boolean =
                !!lot.fromDatetime && moment(lot.fromDatetime).unix() > nowUnix;
            const targetIso: string | null | undefined = isUpcoming
                ? lot.fromDatetime
                : lot.toDatetime;
            if (!targetIso) {
                next[lot.auctionLotId] = '';
                nextSeconds[lot.auctionLotId] = 0;
                continue;
            }
            const targetUnix: number = moment(targetIso).unix();
            const remaining: number = Math.max(targetUnix - nowUnix, 0);
            // >0 on the previous tick and 0 now: the lot ended live on this
            // page. A lot first seen at 0 (already closed on load) never fires.
            if (
                !isUpcoming &&
                remaining === 0 &&
                (this.secondsLeft[lot.auctionLotId] ?? 0) > 0 &&
                this.announcedEnds.get(lot.auctionLotId) !== targetIso
            ) {
                this.announcedEnds.set(lot.auctionLotId, targetIso);
                endedLotIds.push(lot.auctionLotId);
            }
            nextSeconds[lot.auctionLotId] = remaining;
            next[lot.auctionLotId] = formatCountdown(
                targetUnix,
                nowUnix,
                isUpcoming,
            );
        }
        this.countdowns = next;
        this.secondsLeft = nextSeconds;
        for (const id of endedLotIds) this.lotEndedSubject.next(id);
    }
}
